import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { usePropertiesList } from "./use-properties-list";
import { PropertiesFilters } from "./PropertiesFilters";
import { PropertiesTable } from "./PropertiesTable";
import { PropertiesTableSkeleton } from "./PropertiesTableSkeleton";
import { PropertiesListEmpty } from "./PropertiesListEmpty";
import { PropertiesPagination } from "./PropertiesPagination";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "./constants";
import type { PropertyStatusFilter } from "./types";

export function PropertiesPage() {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<PropertyStatusFilter>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const filters = {
    query: debouncedQuery,
    status,
    page,
    limit: DEFAULT_PAGE_SIZE,
  };

  const { data, isLoading, isFetching, isError } = usePropertiesList(filters);
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  // Derived from the *debounced* query on purpose — it must agree with the
  // query that produced `data`. Using the raw `query` would flash the "no
  // results" empty-state during the debounce window. `PropertiesFilters`
  // computes its own `hasFilters` from the raw query (to toggle the reset
  // button instantly); the two are intentionally out of phase.
  const hasFilters = debouncedQuery.length > 0 || status !== "all";

  const handleReset = () => {
    setQuery("");
    setStatus("all");
  };

  return (
    <div data-testid="properties-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("properties.eyebrow")}</p>
        <h1 className="text-4xl">{t("properties.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("properties.subtitle")}
        </p>
      </header>

      <PropertiesFilters
        query={query}
        status={status}
        onQueryChange={setQuery}
        onStatusChange={setStatus}
        onReset={handleReset}
      />

      {isLoading && <PropertiesTableSkeleton />}

      {isError && (
        <div
          data-testid="properties-list-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("properties.list.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <PropertiesListEmpty hasFilters={hasFilters} />
      )}

      {!isError && items.length > 0 && (
        <div
          data-testid="properties-list-container"
          aria-busy={isFetching}
          className={isFetching ? "opacity-70 transition-opacity" : ""}
        >
          <PropertiesTable items={items} />
          <div className="mt-4">
            <PropertiesPagination
              page={page}
              limit={DEFAULT_PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
